import { cipher, decipher } from './Encryption';
/**
 * CookieManager is a utility object for managing cookies with encryption support.
 * It provides methods to set, get, delete, and manage cookies with optional attributes.
 *
 * @property {string} encryptionSecret - The secret key used for encrypting and decrypting cookie values.
 *
 * @method setCookie
 * Sets a cookie with the specified name, value, and optional attributes.
 * If the cookie already exists, it updates its value.
 * @param {string} name - The name of the cookie.
 * @param {string} value - The value to be stored in the cookie.
 * @param {Object} [options] - Optional cookie attributes (e.g., expires, path, domain, etc.).
 * @param {Date} [options.expires] - The expiration date of the cookie.
 * @param {string} [options.path] - The path where the cookie is accessible.
 * @param {string} [options.domain] - The domain where the cookie is accessible.
 *
 * @method getCookie
 * Retrieves the value of a cookie by name.
 * @param {string} name - The name of the cookie to retrieve.
 * @returns {string | undefined} - The cookie value or undefined if not found.
 *
 * @method getCookieAttributes
 * Converts cookie attributes to a string.
 * @param {Object} [options] - Optional cookie attributes.
 * @param {Date} [options.expires] - The expiration date of the cookie.
 * @param {string} [options.path] - The path where the cookie is accessible.
 * @param {string} [options.domain] - The domain where the cookie is accessible.
 * @returns {string} - A string representation of cookie attributes.
 *
 * @method deleteCookie
 * Deletes a cookie by setting its expiration date to the past.
 * @param {string} name - The name of the cookie to delete.
 *
 * @method deleteAllCookies
 * Deletes all cookies by setting their expiration dates to the past.
 */
export const CookieManager = {
  encryptionSecret: process.env.REACT_APP_FPOO_COOKIE_SECRET ?? 'mySecretSalt',
  setCookie: (
    name: string,
    value: string,
    options?: { expires?: Date; path?: string; domain?: string },
  ): void => {
    const myCipher = cipher(CookieManager?.encryptionSecret);
    document.cookie =
      encodeURIComponent(name) +
      '=' +
      encodeURIComponent(myCipher(value)) +
      ';' +
      CookieManager?.getCookieAttributes(options);
  },
  getCookie: (name: string): string | undefined => {
    const cookies = document.cookie.split(';');
    const myDecipher = decipher(CookieManager?.encryptionSecret);
    for (const cookie of cookies) {
      const [cookieName, cookieValue] = cookie.split('=');
      if (cookieName.trim() === name) {
        return cookieValue.trim() ? myDecipher(decodeURIComponent(cookieValue.trim())) : null;
      }
    }
    return undefined;
  },
  getCookieAttributes: (options?: { expires?: Date; path?: string; domain?: string }): string => {
    const attributes = [];
    if (options) {
      if (options.expires) {
        const expirationDate = new Date(options.expires);
        attributes.push(`expires=${expirationDate.toUTCString()}`);
      }
      if (options.path) {
        attributes.push(`path=${options.path}`);
      }
      if (options.domain) {
        attributes.push(`domain=${options.domain}`);
      }
    }
    attributes.push(`Secure`);
    attributes.push(`SameSite=None`);
    return attributes.join('; ');
  },
  deleteCookie: (name: string): void => {
    CookieManager?.setCookie(name, '', {
      expires: new Date('01 Jan 1970 00:00:00 GMT'),
    });
  },
  deleteAllCookies: (): void => {
    document?.cookie
      ?.split(';')
      ?.forEach(
        (cookie) =>
          (document.cookie =
            cookie?.split('=')[0] + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/'),
      );
  },
};
/**
 * Retrieves a cookie by name.
 * @function
 * @param {string} name - The name of the cookie to retrieve.
 * @returns {string | null} The value of the cookie, or null if not found.
 */
export const getCookie = CookieManager.getCookie.bind(CookieManager);

/**
 * Sets a cookie with the specified name and value.
 * @function
 * @param {string} name - The name of the cookie to set.
 * @param {string} value - The value of the cookie.
 * @param {Object} [options] - Optional settings for the cookie (e.g., expires, path).
 */
export const setCookie = CookieManager.setCookie.bind(CookieManager);

/**
 * Deletes a cookie by name.
 * @function
 * @param {string} name - The name of the cookie to delete.
 */
export const deleteCookie = CookieManager.deleteCookie.bind(CookieManager);

/**
 * Deletes all cookies.
 * @function
 */
export const deleteAllCookies = CookieManager.deleteAllCookies.bind(CookieManager);
